import React from 'react';
import { PropTypes } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  map, slice, indexOf, debounce, flatten, get, orderBy,
} from 'lodash';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import Sifter from 'sifter';
import { format } from '@origo_vl_banking/utils';
import { ArrowDown, ArrowUp } from '@origo_vl_banking/icons';

import TableLoader from '../loader/Loader';
import NoData from './NoData';
import TableButton from './TableButton';
import { Button } from '../button';
import { TextInput } from '../text-input';

import {
  ActionItem,
  ActionItemText,
  ActionMenu,
  ActionsWrapper,
  ButtonCell,
  ButtonGroup,
  DetailsButton,
  DetailsButtonCell,
  ExpandedItem,
  ExpandedRow,
  ExpandedMobileItems,
  ExpandedMobileItem,
  ExpandedMobileItemBody,
  ExpandedMobileItemLabel,
  ExpandedItemDetails,
  LoaderContainer,
  StyledDataCell,
  StyledFooterCell,
  StyledTable,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableHeaderRow,
  TableRow,
  Warning,
} from './styled-components';

import { EditActions, SortDirection } from './components';

import Search from './Search';
import Pager from './Pager';

class Table extends React.Component {
  constructor() {
    super();

    this.state = {
      expandedRows: [],
      currentPage: 1,
      menu: null,
      inSearch: false,
      isAndroid: navigator.userAgent.toLowerCase().indexOf('android') > -1,
      width: window.innerWidth,
      editMode: false,
      editValues: {},
      sortedColumn: null,
      sortDirection: 'ASC',
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleCloseMenu);
    window.addEventListener('resize', this.debounceHandleCloseMenu);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleCloseMenu);
    window.removeEventListener('resize', this.debounceHandleCloseMenu);
  }

  handleCloseMenu = (e) => {
    const isTargetMenu = e.target.id === 'TableActionButton' || e.target.id === 'ActionItem';
    if (this.state.menu !== null && !isTargetMenu) {
      this.setState({ menu: null });
    }
  };

  handleResize = () => {
    /** Some android phones were having an issue with the width being changed as a result
     * of the soft keyboard being opened while editing.
     * This caused the keyboard to close as soon as an input field received focus.
     *
     * To prevent this, isAndroidEditMode was introduced.
     * This prevents the width state from changing while editing fields on an Android device.
     * */

    const { editMode, isAndroid } = this.state;
    const newWidth = window.innerWidth;
    const isAndroidEditMode = editMode && isAndroid;
    if (!isAndroidEditMode) {
      this.setState(prevState => ({
        width: prevState.width !== newWidth ? newWidth : prevState.width,
      }));
    }
  };

  debounceHandleCloseMenu = debounce(e => this.handleResize(e), 100);

  onSearch = (filteredData) => {
    const { data, onSearchCallback } = this.props;
    const inSearch = filteredData.length !== data.length;
    if (onSearchCallback) {
      onSearchCallback(filteredData, inSearch);
    }
    this.setState({ filteredData, inSearch });
  };

  handleRowClick(rowId) {
    const currentExpandedRows = this.state.expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter(id => id !== rowId)
      : currentExpandedRows.concat(rowId);

    this.setState({ expandedRows: newExpandedRows });
  }

  handlePageArrow = (action) => {
    const { currentPage } = this.state;
    const { data, itemsPerPage } = this.props;
    const pageCount = Math.ceil(data.length / itemsPerPage);
    let newPage = currentPage;

    if (action === 'increment') {
      newPage += 1;
    } else if (action === 'decrement') {
      newPage -= 1;
    } else if (action === 'firstPage') {
      newPage = 1;
    } else if (action === 'lastPage') {
      newPage = pageCount;
    } else {
      newPage = action;
    }

    if (newPage !== 0 && newPage <= pageCount) {
      this.setState({ currentPage: newPage });
    }
  };

  handleChangeValue = (id, row, e, currentVal) => {
    const { editValues } = this.state;

    const { value } = e.target;

    if (currentVal !== value) {
      editValues[id] = { ...editValues[id], [row]: value };

      this.setState({ editValues });
      e.target.value = null;
    }
  };

  handleSave = () => {
    const { handleSave } = this.props;
    const { editValues } = this.state;

    if (Object.entries(editValues).length > 0) {
      handleSave(editValues);
    }
    this.cancelEditMode(true);
  };

  cancelEditMode = (saveMode = false) => {
    const { onEdit } = this.props;
    const { editValues } = this.state;
    this.setState({
      editMode: false,
      editValues: {},
    });
    if (typeof saveMode === 'boolean' && saveMode === true) {
      onEdit.onSave(editValues);
    } else {
      onEdit.onCancel();
    }
  };

  formatData = (data) => {
    const formattedData = [];
    data.forEach((item) => {
      const formattedItem = {};
      Object.entries(item).forEach(([key, value]) => {
        formattedItem[key] = format.value(value);
      }, this);
      formattedData.push(formattedItem);
    }, this);
    return formattedData;
  };

  filteredItems = () => {
    const { data, columns, idField } = this.props;
    const formattedData = this.formatData(data);

    const sifter = new Sifter(formattedData);
    const result = sifter.search(this.state.searchTerm, { fields: columns });

    const results = result.items.map(item => data[get(item, idField)]);

    return results;
  };

  showWarning = (value) => {
    if (typeof value === 'number') {
      return value < 0;
    }

    if (
      value instanceof Date
      || moment(value, 'YYYY-MM-DDTHH:mm:ss', true).isValid()
      || moment(value, 'YYYY-MM-DDTHH:mm:ss.SSSSSS', true).isValid()
    ) {
      const date = moment(value).format('YYYY-MM-DD');
      const now = moment().format('YYYY-MM-DD');
      return moment(now).isAfter(date);
    }

    return false;
  };

  sortHandler = (fieldHeader) => {
    const { sortDirection, sortedColumn } = this.state;
    const newSortDirection = () => {
      if (sortedColumn !== fieldHeader) {
        return 'asc';
      }

      if (sortDirection === 'asc') {
        return 'desc';
      }
      if (sortDirection === 'desc') {
        return 'off';
      }

      return 'asc';
    };

    this.setState({
      sortedColumn: fieldHeader,
      sortDirection: newSortDirection(),
      expandedRows: [],
    });
  };

  renderSelectHeader = () => {
    const {
      data, expand, handleSelectAll, selectedItem, hideCheckbox,
    } = this.props;

    if (hideCheckbox) {
      return null;
    }

    const key = `select-header-${uuidv4()}`;

    return (
      <TableHeaderItem key={key} padding="10px" selectable expand={expand}>
        <input
          checked={selectedItem.length > 0}
          onChange={() => handleSelectAll(data)}
          type="checkbox"
        />
      </TableHeaderItem>
    );
  };

  renderHeaders(name, items, mobileColumns) {
    const {
      fixed,
      firstColumnWidth,
      rightAlign,
      columnsWidth,
      columns,
      labelPrefix,
      mobileFixed,
      customColspan,
      mobileSize,
      sortable,
      mobileColumnsWidth,
      expand,
    } = this.props;
    const { width, sortedColumn, sortDirection } = this.state;
    const columnHeaders = this.state.width <= (mobileSize || 580) ? mobileColumns : columns;

    return columnHeaders.map((item, index) => {
      const right = (this.props.data[0] && typeof get(this.props.data[0], item) === 'number')
        || (rightAlign && rightAlign.indexOf(item) !== -1);
      const key = `${item}.${index}`;
      const columnWidth = columnsWidth.length === columns.length && columnsWidth[index];

      const mobileColumnWidth = mobileColumnsWidth.length === mobileColumns.length ? mobileColumnsWidth[index] : null;
      const finalWidth = width > (mobileSize || 580) ? columnWidth : mobileColumnWidth;
      const colSpan = customColspan.length === columns.length ? customColspan[index] : null;

      return (
        <TableHeaderItem
          fixed={fixed}
          firstColumnWidth={firstColumnWidth}
          last={index === columnHeaders.length - 1 && right}
          first={index === 0}
          alignRight={right}
          mobile={mobileColumns.indexOf(item) !== -1}
          mobileFixed={mobileFixed}
          key={key}
          colSpan={colSpan}
          columnWidth={finalWidth}
          sortable={sortable}
          onClick={() => sortable && this.sortHandler(item)}
          expand={expand}
        >
          <>
            <FormattedMessage id={labelPrefix ? `${labelPrefix}.${item}` : `${name}.TH.${item}`} />
            <SortDirection
              sortDirection={sortDirection}
              sortedColumn={sortedColumn}
              columnName={columnHeaders[index]}
            />
            {/* {sortedColumn === columnHeaders[index] && ' ▼'} */}
          </>
        </TableHeaderItem>
      );
    });
  }

  renderItem(rowItem, isFooter = false, itemIndex = -1) {
    const {
      // columns,
      actions,
      actionButton,
      borderColor,
      button,
      clickAction,
      conditionalColumns,
      customColspan,
      details,
      dense,
      editable,
      editableColumns,
      editableCondition,
      expand,
      expandButton,
      footerMessageId,
      hideCheckbox,
      hideMobileDetailsHeader,
      hideMobileTable,
      idField,
      labelPrefix,
      mobileSize,
      name,
      onAction,
      onEdit,
      overwriteGlobalCSS,
      percentageColumn,
      privateMode,
      rightAlign,
      mobileDetailsStyle,
      selectable,
      selectedItem,
      selectiveId,
      whiteSpace,
      conditionalRowStyling,
      selectableCondition,
      expandedMobileItemStyle,
    } = this.props;
    const inMobile = this.state.width <= (mobileSize || 580);

    const mobileColumns = this.props.mobileColumns.length > 0 ? this.props.mobileColumns : this.props.columns;
    const columns = inMobile && inMobile ? mobileColumns : this.props.columns;

    const detailColumns = this.props.columns.filter(
      column => this.props.mobileColumns.indexOf(column) === -1,
    );
    const selected = selectedItem && indexOf(selectedItem, get(rowItem, idField)) !== -1;

    const expandedComponentColspan = columns.length + expand + selectable;

    const mappedIdField = get(rowItem, idField) === undefined ? itemIndex : get(rowItem, idField);

    const clickCallback = () => this.handleRowClick(mappedIdField);
    const RenderCell = isFooter ? StyledFooterCell : StyledDataCell;
    const value = (field) => {
      if (percentageColumn.includes(field) && typeof get(rowItem, field) === 'number') {
        return `${format.value(get(rowItem, field) * 100)}%`;
      }

      if (!isFooter && this.state.editMode && editableColumns.indexOf(field) !== -1) {
        const id = mappedIdField;
        const fieldName = `data.${itemIndex}.${field}`;
        const val = this.state.editValues[id] && this.state.editValues[id][field]
          ? this.state.editValues[id][field]
          : get(rowItem, field);
        onEdit.onStart();
        return (val !== undefined && editable && editableCondition && editableCondition(rowItem)) ? (
          <TextInput
            name={fieldName}
            value={val}
            id={fieldName}
            height={dense ? '0px' : '22px'}
            fontSize={dense ? '14px' : '16px'}
            onBlur={e => this.handleChangeValue(id, field, e, val)}
          /> ) : (format.value(get(rowItem, field))
        );
      }

      const isDate = moment(get(rowItem, field), 'YYYY-MM-DDTHH:mm:ss', true).isValid();
      const conditional = conditionalColumns.indexOf(field) !== -1;

      const displayValue = isDate && inMobile < (mobileSize || 580)
        ? format.shortDate(get(rowItem, field))
        : format.value(get(rowItem, field));

      if (conditional && this.showWarning(get(rowItem, field))) {
        return <Warning selected={selected}>{displayValue}</Warning>;
      }

      return displayValue;
    };

    const shouldRenderCheckbox = selectableCondition ? rowItem[selectableCondition] : true;
    const cells = () => map(columns, (field, index) => {
      const colSpan = customColspan.length === columns.length ? customColspan[index] : null;

      return (
        <RenderCell
          last={index === columns.length - 1}
          alignRight={
              typeof get(rowItem, field) === 'number'
              || (rightAlign && rightAlign.indexOf(field) !== -1)
            }
          key={`cell.${index}`}
          mobile={mobileColumns.indexOf(field) !== -1}
          privateMode={typeof get(rowItem, field) === 'number' ? privateMode : false}
          mobileSize={mobileSize}
          isExpanded={this.state.expandedRows.includes(mappedIdField)}
          hideMobileTable={hideMobileTable}
          editMode={this.state.editMode && !isFooter}
          colSpan={colSpan}
          onClick={() => {
            if (selectable && shouldRenderCheckbox && hideCheckbox) {
              return clickAction(rowItem);
            }

            return null;
          }}
          whiteSpace={whiteSpace}
          dense={dense}
          id={isFooter ? 'footer' : `cell.${index}`}
        >
          {isFooter && footerMessageId && index === 0 && (
          <FormattedMessage id={`${name}.${footerMessageId}`} />
          )}
          {value(field, index)}
        </RenderCell>
      );
    });

    const CustomExpandButton = String(expandButton).includes('createElement') ? expandButton : null;
    const ActionButton = actionButton;

    const getSelective = (selectiveIdList) => {
      if (typeof selectiveIdList === 'string') {
        return get(rowItem, selectiveIdList);
      }
      if (Array.isArray(selectiveIdList)) {
        let noContent = false;
        selectiveIdList.map((id) => {
          const item = get(rowItem, id);
          if (item !== undefined && item !== null) {
            noContent = true;
          }
        });
        return noContent;
      }
      return selectiveIdList;
    };

    const conditionalRowStyle = conditionalRowStyling && conditionalRowStyling(rowItem);
    const tableRowKey = `row-data-${mappedIdField} - ${uuidv4()}`;
    const itemRows = [
      <TableRow
        expand={expand}
        borderColor={borderColor}
        selectable={selectable}
        hideCheckbox={hideCheckbox}
        inMobile={inMobile}
        selected={selected}
        key={tableRowKey}
        even={itemIndex % 2 === 0}
        conditionalRowStyle={conditionalRowStyle}
        id={isFooter ? 'footer' : tableRowKey}
      >
        {selectable && !hideCheckbox && !inMobile && (
          <StyledDataCell
            footer={isFooter}
            onClick={() => shouldRenderCheckbox && clickAction(rowItem)}
            whiteSpace={whiteSpace}
            dense={dense}
          >
            {!isFooter && shouldRenderCheckbox && (
              <input type="checkbox" readOnly checked={selected} />
            )}
          </StyledDataCell>
        )}
        {cells()}
        {button && actions && !isFooter && (
          <ButtonCell
            id="ButtonCell"
            width={25}
            onClick={() => this.setState({
              menu: this.state.menu === rowItem ? null : rowItem,
            })}
          >
            {this.state.menu === rowItem && (
              <ActionMenu id="ActionMenu">
                <div>
                  {actions.map((action, index) => {
                    const key = `index.${index}`;
                    return (
                      <ActionItem
                        key={key}
                        id="ActionItem"
                        onClick={() => {
                          if (action.payload === 'expand') {
                            this.handleRowClick(mappedIdField);
                          } else {
                            onAction(action.payload, rowItem);
                          }
                        }}
                      >
                        <ActionItemText>
                          {action.text && (
                            <FormattedMessage
                              id={
                                action.payload === 'expand'
                                && this.state.expandedRows.includes(mappedIdField)
                                  ? 'Collapse'
                                  : action.text
                              }
                            />
                          )}
                        </ActionItemText>
                      </ActionItem>
                    );
                  })}
                </div>
              </ActionMenu>
            )}
            <TableButton id="TableActionButton" width={25} />
          </ButtonCell>
        )}
        {selectiveId && isFooter ? <RenderCell /> : null}
        {actionButton && !inMobile && <ActionButton {...rowItem} />}
        {// This is where the expanded button is rendered
        !expandButton ? (
          <DetailsButtonCell
            expand={expand}
            mobileSize={mobileSize}
            overwriteGlobalCSS={overwriteGlobalCSS}
            mobileColumns={detailColumns.length > 0 && !isFooter}
            isFooter={isFooter}
            selective={selectiveId}
            show={
              (selectiveId && getSelective(selectiveId))
              || expand
              || (inMobile && actions)
              || (inMobile && detailColumns.length > 0)
            }
          >
            <DetailsButton
              show={
                (selectiveId && getSelective(selectiveId))
                || expand
                || (inMobile && actions)
                || (inMobile && detailColumns.length > 0)
              }
              overwriteGlobalCSS={overwriteGlobalCSS}
              onClick={clickCallback}
            >
              {this.state.expandedRows.includes(mappedIdField) ? (
                <ArrowUp size={15} />
              ) : (
                <ArrowDown size={15} />
              )}
            </DetailsButton>
          </DetailsButtonCell>
        ) : (
          <CustomExpandButton
            onClick={clickCallback}
            isOpen={this.state.expandedRows.includes(mappedIdField)}
          />
        )
}
      </TableRow>,
    ];

    const includedExpandedRow = this.state.expandedRows.includes(mappedIdField);
    // && (expand || selectiveId)
    if (includedExpandedRow) {
      // This is where the expanded component is rendered
      // Hidden columns in mobile mode are also rendered here
      let hasDetailData = false;
      detailColumns.forEach((column) => {
        if (get(rowItem, column) !== undefined) {
          hasDetailData = true;
        }
      });
      itemRows.push(
        // eslint-disable-line
        <ExpandedRow
          show={inMobile || includedExpandedRow}
          even={itemIndex % 2 === 0}
          key={`row-expanded-${mappedIdField}`}
          colS
        >
          <ExpandedItem
            overwriteGlobalCSS={overwriteGlobalCSS}
            even={itemIndex % 2 === 0}
            colSpan={expandedComponentColspan + 1}
          >
            {inMobile && hasDetailData && (
              <ExpandedMobileItems expandedMobileItemStyle={expandedMobileItemStyle} hideMobileTable={hideMobileTable} mobileSize={mobileSize}>
                {!hideMobileDetailsHeader && (
                  <h3 style={{ paddingLeft: 12 }}>
                    <FormattedMessage id="Table.Mobile.Details" />
                  </h3>
                )}
                <ExpandedMobileItemBody style={mobileDetailsStyle}>
                  {detailColumns.map(
                    column => get(rowItem, column) !== undefined && ( // labelPrefix
                    <ExpandedMobileItem key={column}>
                      <ExpandedMobileItemLabel>
                        {/* <FormattedMessage id={`${name}.TH.${column}`} />: */}
                        <FormattedMessage
                          id={labelPrefix ? `${labelPrefix}.${column}` : `${name}.TH.${column}`}
                        />
                      </ExpandedMobileItemLabel>
                      <ExpandedMobileItemLabel>
                        {format.value(get(rowItem, column))}
                      </ExpandedMobileItemLabel>
                    </ExpandedMobileItem>
                    ),
                  )}
                </ExpandedMobileItemBody>
                {actionButton && <ActionButton {...rowItem} />}
              </ExpandedMobileItems>
            )}
            {actions && (
              <ButtonGroup>
                {actions.map(action => (
                  <div style={{ padding: 8 }} key={`${action.text}-div`}>
                    <Button
                      content={
                        action.payload === <FormattedMessage id="expand" /> ? (
                          <FormattedMessage id="Collapse" />
                        ) : (
                          <FormattedMessage id={action.text} />
                        )
                      }
                      onClick={() => {
                        if (action.payload === 'expand') {
                          this.handleRowClick(get(rowItem, mappedIdField));
                        } else {
                          onAction(action.payload, rowItem);
                        }
                      }}
                    />
                  </div>
                ))}
              </ButtonGroup>
            )}
            <ExpandedItemDetails
              show={expand || (selectiveId && getSelective(selectiveId)) || actions}
            >
              {details && details(rowItem)}
            </ExpandedItemDetails>
          </ExpandedItem>
        </ExpandedRow>,
      );
    }

    return itemRows;
  }

  render() {
    const {
      columns,
      customBorder,
      data,
      editable,
      expand,
      fixed,
      footerData,
      formattedPlaceholder,
      hideCheckbox,
      idField,
      itemsPerPage,
      loader,
      loading,
      mobileSize,
      name,
      nestedField,
      nestedSearch,
      noPagerBackground,
      paging,
      search,
      searchConjunction,
      searchPlaceholder,
      selectable,
      sortable,
      searchHeight,
      totalRows,
    } = this.props;
    const {
      currentPage, sortedColumn, sortDirection, filteredData,
    } = this.state;
    const inMobile = this.state.width <= (mobileSize || 580);
    const mobileColumns = this.props.mobileColumns.length ? this.props.mobileColumns : columns;

    let allItemRows = [];
    const tableHeaders = [];

    // Either data from search results or data from props
    const items = search && filteredData ? filteredData : data;

    // Paging variables
    const start = paging ? (currentPage - 1) * itemsPerPage : 0; // 0
    const end = paging ? currentPage * itemsPerPage : data.length; // 8
    const pageCount = Math.ceil(items.length / itemsPerPage);

    // slicedData is the paged data. Not paged when search results are being displayed
    let rawData = items;

    // Data is sorted if the sortable flag is passed
    if (sortable && sortedColumn && sortDirection !== 'off') {
      rawData = orderBy(rawData, [sortedColumn], [sortDirection]);
    } else if (sortDirection === 'off') {
      rawData = items;
    }

    const slicedData = slice(rawData, start, end);

    if (selectable && !hideCheckbox && !inMobile) {
      tableHeaders.push(this.renderSelectHeader());
    }

    tableHeaders.push(this.renderHeaders(name, columns, mobileColumns));
    const flattenedHeaders = flatten(tableHeaders);
    if (expand) {
      flattenedHeaders.push(<th key="headers" />);
    }

    // Render rows from slicedData
    slicedData.forEach((item) => {
      // Item Index used to find even & odd rows
      const itemIndex = slicedData.indexOf(item) + 1;
      // Row rendered without a footer
      const perItemRows = this.renderItem(item, false, itemIndex);
      // Added to row array
      allItemRows = allItemRows.concat(perItemRows);
    });

    let footerRow = [];
    if (footerData !== null) {
      footerRow = this.renderItem(footerData, true);
    }

    const Loader = loader || React.createElement(TableLoader, { size: 70 }, null);
    const table = (
      <>
        <ActionsWrapper search={search} edit={editable} editMode={this.state.editMode}>
          {search && (
            <Search
              placeholder={searchPlaceholder}
              columns={columns}
              data={data}
              searchConjunction={searchConjunction}
              onSearch={this.onSearch}
              name={`${name}'_search'`}
              idField={idField}
              nestedField={nestedField}
              nestedSearch={nestedSearch}
              formattedPlaceholder={formattedPlaceholder}
              height={searchHeight}
            />
          )}
          {editable && (
            <EditActions
              setState={e => this.setState(e)}
              editMode={this.state.editMode}
              handleSave={this.handleSave}
              handleCancel={this.cancelEditMode}
              search={search}
            />
          )}
        </ActionsWrapper>
        <StyledTable fixed={fixed}>
          <TableHeader customBorder={customBorder}>
            <TableHeaderRow>{flattenedHeaders}</TableHeaderRow>
          </TableHeader>
          {!loading && data.length > 0 && (
            <TableBody>
              {allItemRows}
              {footerRow.length > 0 && footerRow}
            </TableBody>
          )}
        </StyledTable>
        {loading && <LoaderContainer>{Loader}</LoaderContainer>}
        {!loading && (data.length < 1 || items.length < 1) && <NoData />}
        {paging && pageCount > 1 && (
          <Pager
            pageCount={pageCount}
            totalRows={totalRows}
            currentPage={currentPage}
            handlePageArrow={this.handlePageArrow}
            noPagerBackground={noPagerBackground}
          />
        )}
      </>
    );

    return table;
  }
}

Table.propTypes = {
  actions: PropTypes.array,
  borderColor: PropTypes.string,
  button: PropTypes.bool,
  clickAction: PropTypes.func,
  columns: PropTypes.array.isRequired,
  columnsWidth: PropTypes.array,
  conditionalColumns: PropTypes.array,
  customBorder: PropTypes.string,
  customColspan: PropTypes.array,
  data: PropTypes.array.isRequired,
  details: PropTypes.func,
  dense: PropTypes.bool,
  editable: PropTypes.bool,
  editableColumns: PropTypes.array,
  expand: PropTypes.bool,
  expandButton: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  firstColumnWidth: PropTypes.string,
  fixed: PropTypes.bool,
  footerData: PropTypes.object,
  footerMessageId: PropTypes.string,
  handleSave: PropTypes.func,
  handleSelectAll: PropTypes.func,
  idField: PropTypes.string,
  itemsPerPage: PropTypes.number,
  labelPrefix: PropTypes.string,
  loading: PropTypes.bool,
  loader: PropTypes.elementType,
  hideMobileDetailsHeader: PropTypes.bool,
  hideMobileTable: PropTypes.bool,
  mobileColumns: PropTypes.array,
  mobileColumnsWidth: PropTypes.array,
  mobileFixed: PropTypes.bool,
  name: PropTypes.string,
  onAction: PropTypes.func,
  onEdit: PropTypes.shape({
    onStart: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
  }),
  onSearchCallback: PropTypes.func,
  overwriteGlobalCSS: PropTypes.bool,
  paging: PropTypes.bool,
  percentageColumn: PropTypes.array,
  privateMode: PropTypes.bool,
  rightAlign: PropTypes.array,
  search: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  selectable: PropTypes.bool,
  selectedItem: PropTypes.array,
  selectiveId: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  mobileSize: PropTypes.number,
  formattedPlaceholder: PropTypes.bool,
  sortable: PropTypes.bool,
  hideCheckbox: PropTypes.bool,
  nestedField: PropTypes.string,
  nestedSearch: PropTypes.bool,
  noPagerBackground: PropTypes.bool,
  searchConjunction: PropTypes.string,
  mobileDetailsStyle: PropTypes.object,
  searchHeight: PropTypes.string,
  whiteSpace: PropTypes.string,
  conditionalRowStyling: PropTypes.func,
  selectableCondition: PropTypes.string,
  totalRows: PropTypes.number,
  editableCondition: PropTypes.func,
  expandedMobileItemStyle: PropTypes.object,
};

Table.defaultProps = {
  actions: null,
  borderColor: null,
  button: false,
  clickAction: () => null,
  columnsWidth: [],
  conditionalColumns: [],
  conditionalRowStyling: () => null,
  searchConjunction: 'or',
  customBorder: null,
  dense: false,
  nestedField: 'Details',
  nestedSearch: false,
  expandedMobileItemStyle: {},
  customColspan: [],
  details: null,
  editable: false,
  editableColumns: null,
  expand: false,
  expandButton: null,
  firstColumnWidth: 'auto',
  fixed: false,
  footerData: null,
  footerMessageId: '',
  formattedPlaceholder: false,
  handleSave: () => null,
  handleSelectAll: () => null,
  hideMobileDetailsHeader: false,
  hideMobileTable: false,
  mobileDetailsStyle: {},
  hideCheckbox: false,
  idField: 'RowId',
  itemsPerPage: 50,
  labelPrefix: null,
  loader: null,
  loading: false,
  mobileColumns: [],
  mobileColumnsWidth: [],
  mobileFixed: false,
  mobileSize: 580,
  name: 'Table',
  onAction: null,
  onEdit: { onStart: () => null, onSave: () => null, onCancel: () => null },
  onSearchCallback: null,
  overwriteGlobalCSS: false,
  paging: false,
  percentageColumn: [],
  privateMode: false,
  rightAlign: null,
  search: false,
  searchPlaceholder: null,
  selectable: false,
  selectedItem: [],
  selectiveId: '',
  sortable: true,
  noPagerBackground: true,
  searchHeight: null,
  whiteSpace: 'pre-line',
  selectableCondition: null,
  totalRows: null,
  editableCondition: () => true,
};

export default Table;
